import { FC, useCallback, useMemo, useState } from 'react'
import ExternalLink from '../external-link'
import GYGActivityWidget from '../gyg-activity-widget'
import { useLocale, useTranslation } from '../../../hooks/locale'
import { GYG_SHOW_ALL_BASE, toGygLocale } from '../../../utils/gyg-utils'

import {
    Section,
    Container,
    TitleContainer,
    Title,
    Subtitle,
    VisuallyHidden,
    WidgetWrapper,
    WidgetScaler,
    ShowAllContainer,
    ShowAllLink,
    NewTagContainer,
} from './styles'

import { NewTagIcon, NewTagText } from '../../app-components/filter-sort-section/styles'

type Props = {
    destinationName: string
    partnerId: string
    campaign: string
    items?: number
    compact?: boolean
    showNewTag?: boolean
}

const GYGExperiencesSection: FC<Props> = ({ destinationName, partnerId, campaign, items, compact, showNewTag }) => {
    const { locale } = useLocale()
    const { t } = useTranslation()
    const [hasError, setHasError] = useState(false)

    // Stable reference — avoids GYGActivityWidget re-rendering on every parent render.
    const handleError = useCallback(() => setHasError(true), [])

    // new URL() + URLSearchParams handles encoding automatically and is easier to
    // audit for missing/extra params than a hand-rolled template literal.
    const showAllUrl = useMemo(() => {
        const url = new URL(GYG_SHOW_ALL_BASE)
        url.searchParams.set('q', destinationName)
        url.searchParams.set('partner_id', partnerId)
        url.searchParams.set('cmp', campaign)
        url.searchParams.set('locale', toGygLocale(locale))
        return url.toString()
    }, [destinationName, partnerId, campaign, locale])

    if (hasError) {
        return null
    }

    return (
        <Section compact={compact}>
            <Container compact={compact}>
                <TitleContainer>
                    {showNewTag && (
                        <NewTagContainer aria-hidden="true">
                            <NewTagText>{t('new')}</NewTagText>
                            <NewTagIcon />
                        </NewTagContainer>
                    )}
                    <Title>{t('gyg.sectionTitle')}</Title>
                    <Subtitle>{t('gyg.sectionSubtitle')}</Subtitle>
                </TitleContainer>
                <WidgetWrapper>
                    <WidgetScaler>
                        <GYGActivityWidget
                            destinationName={destinationName}
                            partnerId={partnerId}
                            campaign={campaign}
                            locale={locale}
                            items={items}
                            onError={handleError}
                        />
                    </WidgetScaler>
                </WidgetWrapper>
                <ShowAllContainer>
                    <ShowAllLink
                        as={ExternalLink}
                        href={showAllUrl}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        noCrawl
                    >
                        {t('gyg.showAll')}
                        {/* WCAG 2.1 SC 2.4.4: screen readers must announce that
                            the link opens in a new tab; visible text alone does
                            not convey this for target="_blank" links. */}
                        <VisuallyHidden>, opens in new tab</VisuallyHidden>
                    </ShowAllLink>
                </ShowAllContainer>
            </Container>
        </Section>
    )
}

export default GYGExperiencesSection
